import React, { useState, useEffect, useCallback, useRef, createPortal } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import WorkerModal from '../components/WorkerModal';
import CategoryManager from '../components/CategoryManager';
import SaveButton from '../components/SaveButton';
import { ExportMenu } from '../components/ExportMenu';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { colorTheme, cycleColorTheme, styleTheme, cycleStyleTheme } = useTheme();
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(() => localStorage.getItem('wf_search') || '');
  const [statusFilter, setStatusFilter] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('wf_status_filter')); return Array.isArray(s) ? s : []; } catch { return []; }
  });
  const [categoryFilter, setCategoryFilter] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('wf_category_filter')); return Array.isArray(s) ? s : []; } catch { return []; }
  });
  const [tempStatusFilter, setTempStatusFilter] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('wf_status_filter')); return Array.isArray(s) ? s : []; } catch { return []; }
  });
  const [tempCategoryFilter, setTempCategoryFilter] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('wf_category_filter')); return Array.isArray(s) ? s : []; } catch { return []; }
  });
  const [showCatDrop, setShowCatDrop] = useState(false);
  const [showStatusDrop, setShowStatusDrop] = useState(false);
  const [filterError, setFilterError] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editWorker, setEditWorker] = useState(null);
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [exportLoading, setExportLoading] = useState('');
  const [selectedBarCat, setSelectedBarCat] = useState(null);
  const [showColToggle, setShowColToggle] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // {message, onConfirm, danger}
  const confirmResolveRef = useRef(null);
  const showConfirm = (message, danger = false) => new Promise(resolve => {
    confirmResolveRef.current = resolve;
    setConfirmModal({ message, danger, onConfirm: () => { resolve(true); setConfirmModal(null); }, onCancel: () => { resolve(false); setConfirmModal(null); } });
  });
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wf_hidden_cols') || '[]'); }
    catch { return []; }
  });
  // MFA setup state
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaQr, setMfaQr] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');

  // Admin panel state
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'iam'
  const [iamTab, setIamTab] = useState('users'); // 'users' | 'database'
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newRole, setNewRole] = useState('read_only');
  const [adminPanelError, setAdminPanelError] = useState('');

  // Admin MFA provisioning modal state
  const [mfaProvisionModal, setMfaProvisionModal] = useState(null); // { username, qrDataUrl, secret }
  const [mfaProvisionLoading, setMfaProvisionLoading] = useState(false);

  // Manage Database state
  const [showManageDb, setShowManageDb] = useState(false);
  const [dbLoading, setDbLoading] = useState('');

  // Draggable columns and filters state
  const [colWidths, setColWidths] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('wf_col_widths_v1')) || {};
    } catch { return {}; }
  });
  const [resizingCol, setResizingCol] = useState(null);

  useEffect(() => {
    localStorage.setItem('wf_col_widths_v1', JSON.stringify(colWidths));
  }, [colWidths]);

  useEffect(() => {
    if (!resizingCol) return;
    const handleMouseMove = (e) => {
      const delta = e.clientX - resizingCol.startX;
      const newWidth = Math.max(50, resizingCol.startWidth + delta);
      setColWidths(prev => ({ ...prev, [resizingCol.id]: newWidth }));
    };
    const handleMouseUp = () => setResizingCol(null);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingCol]);

  const handleResizeStart = (e, colId) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingCol({ id: colId, startX: e.clientX, startWidth: colWidths[colId] || DEFAULT_COL_WIDTHS[colId] || 100 });
  };

  const ALL_COLUMN_IDS = ['sno','source','name','category','phone','qualification','experience','salary','preferred_location','secondary_location','preferred_block','client','status','worked_before','resume','comment','last_update','actions'];
  const DEFAULT_COL_WIDTHS = {
    sno: 60, source: 90, name: 140, category: 120, phone: 110, qualification: 120,
    experience: 90, salary: 90, preferred_location: 120, secondary_location: 120, preferred_block: 120, client: 100,
    status: 100, worked_before: 80, resume: 90, comment: 200, last_update: 110, actions: 200
  };
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem('wf_column_order_v6');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.every(col => ALL_COLUMN_IDS.includes(col.id)) && parsed.length === ALL_COLUMN_IDS.length) {
          return parsed;
        }
      } catch (e) {}
    }
    return [
      { id: 'sno', label: 'S.NO' },
      { id: 'source', label: 'SOURCE' },
      { id: 'name', label: 'NAME' },
      { id: 'category', label: 'JOB ROLE' },
      { id: 'phone', label: 'CONTACT' },
      { id: 'qualification', label: 'QUALIFICATION' },
      { id: 'experience', label: 'EXPERIENCE' },
      { id: 'salary', label: 'SALARY' },
      { id: 'preferred_location', label: 'PREF. STATE' },
      { id: 'secondary_location', label: 'PREF. DISTRICT' },
      { id: 'preferred_block', label: 'PREF. BLOCK' },
      { id: 'client', label: 'CLIENT' },
      { id: 'status', label: 'STATUS' },
      { id: 'worked_before', label: 'ALUMNI' },
      { id: 'resume', label: 'RESUME' },
      { id: 'comment', label: 'COMMENT / NOTES' },
      { id: 'last_update', label: 'LAST UPDATE' },
      { id: 'actions', label: 'ACTIONS' }
    ];
  });

  const COL_FILTER_IDS = ['sno','source','name','category','phone','qualification','experience','salary','preferred_location','secondary_location','preferred_block','client','status','worked_before','comment','last_update'];
  const defaultColFilters = () => Object.fromEntries(COL_FILTER_IDS.map(k => [k, []]));
  const [colFilters, setColFilters] = useState(() => {
    const defaults = defaultColFilters();
    try {
      const saved = JSON.parse(localStorage.getItem('wf_col_filters'));
      if (saved && typeof saved === 'object') {
        return COL_FILTER_IDS.reduce((acc, key) => {
          acc[key] = Array.isArray(saved[key]) ? saved[key] : [];
          return acc;
        }, { ...defaults });
      }
    } catch {}
    return defaults;
  });
  const [colSearchTexts, setColSearchTexts] = useState(
    Object.fromEntries(COL_FILTER_IDS.map(k => [k, '']))
  );
  const [activeFilters, setActiveFilters] = useState(
    Object.fromEntries(COL_FILTER_IDS.map(k => [k, false]))
  );

  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.cat-dropdown-container')) setShowCatDrop(false);
      if (!e.target.closest('.status-dropdown-container')) setShowStatusDrop(false);
      if (!e.target.closest('.col-dropdown-container')) setActiveFilters({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    localStorage.setItem('wf_column_order_v6', JSON.stringify(columns));
  }, [columns]);

  useEffect(() => { localStorage.setItem('wf_search', search); }, [search]);
  useEffect(() => { localStorage.setItem('wf_status_filter', JSON.stringify(statusFilter)); }, [statusFilter]);
  useEffect(() => { localStorage.setItem('wf_category_filter', JSON.stringify(categoryFilter)); }, [categoryFilter]);
  useEffect(() => { localStorage.setItem('wf_col_filters', JSON.stringify(colFilters)); }, [colFilters]);

  const loadCategories = async () => {
    const { data } = await axios.get('/api/categories');
    setCategories(data.map(c => c.name));
  };

  const loadMfaStatus = async () => {
    try {
      const { data } = await axios.get('/api/mfa/status');
      setMfaEnabled(!!data.mfa_enabled);
    } catch {}
  };

  const loadEmployees = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    const { data } = await axios.get('/api/employees', { params });
    setEmployees(data);
  }, [search]);

  const loadStats = async () => {
    const { data } = await axios.get('/api/stats');
    setStats(data);
  };

  useEffect(() => { loadCategories(); loadStats(); loadMfaStatus(); }, []);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  // ── Real-time SSE: refresh data whenever another user makes changes ──
  useEffect(() => {
    const token = sessionStorage.getItem('wf_token');
    if (!token) return;
    // Use fetch-based EventSource workaround (token in URL for SSE)
    const url = `/api/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.addEventListener('data_changed', () => {
      loadEmployees();
      loadStats();
    });
    es.onerror = () => {
      // Silently reconnect — browser handles this automatically
    };
    return () => es.close();
  }, []); // eslint-disable-line

  const refresh = () => { loadEmployees(); loadStats(); };

  const loadUsers = async () => {
    try {
      const { data } = await axios.get('/api/users');
      setUsers(data);
      setAdminPanelError('');
    } catch (e) {
      setAdminPanelError(e.response?.data?.error || 'Failed to load users');
    }
  };

  useEffect(() => {
    if (view === 'iam') {
      loadUsers();
    }
  }, [view]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUsername || !newAdminPassword) {
      setAdminPanelError('Username and password are required');
      return;
    }
    try {
      // Step 1: Create the user
      const { data: created } = await axios.post('/api/users', {
        username: newUsername.trim(),
        password: newAdminPassword,
        role: newRole
      });
      const createdUsername = newUsername.trim();
      setNewUsername('');
      setNewAdminPassword('');
      setNewRole('read_only');
      setAdminPanelError('');
      await loadUsers();

      // Step 2: Immediately provision MFA for the new user
      // Re-fetch users list to get the new user's ID
      const { data: freshUsers } = await axios.get('/api/users');
      const newUser = freshUsers.find(u => u.username === createdUsername);
      if (newUser) {
        setMfaProvisionLoading(true);
        try {
          const { data } = await axios.post(`/api/users/${newUser.id}/mfa/provision`);
          setMfaProvisionModal({ username: data.username, qrDataUrl: data.qrDataUrl, secret: data.secret });
          loadUsers(); // refresh to show updated mfa_enabled
        } catch {
          // MFA provisioning optional — don't fail user creation
        } finally {
          setMfaProvisionLoading(false);
        }
      }
    } catch (e) {
      setAdminPanelError(e.response?.data?.error || 'Failed to add user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!await showConfirm('Delete this user account? This cannot be undone.', true)) return;
    try {
      await axios.delete(`/api/users/${userId}`);
      loadUsers();
    } catch (e) {
      setAdminPanelError(e.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleMfaReenroll = async (userId) => {
    if (!await showConfirm('This will invalidate the existing MFA code for this user and generate a new QR. Proceed?')) return;
    setMfaProvisionLoading(true);
    try {
      const { data } = await axios.post(`/api/users/${userId}/mfa/reenroll`);
      setMfaProvisionModal({ username: data.username, qrDataUrl: data.qrDataUrl, secret: data.secret });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Re-enrollment failed');
    } finally {
      setMfaProvisionLoading(false);
    }
  };

  const handleMfaRevoke = async (userId) => {
    if (!await showConfirm('Revoke MFA for this user? They will be able to log in without an authenticator code.', true)) return;
    try {
      await axios.post(`/api/users/${userId}/mfa/revoke`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to revoke MFA');
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      setChangePasswordError('Both fields are required');
      return;
    }
    try {
      await axios.post('/api/change-password', { currentPassword, newPassword });
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setChangePasswordError('');
      toast.success('Password updated successfully!');
    } catch (e) {
      setChangePasswordError(e.response?.data?.error || 'Failed to change password');
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    if (e.target.closest('.filter-btn') || e.target.closest('.col-filter-input') || e.target.closest('.filter-popover') || e.target.closest('.th-filter-chips')) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newColumns = [...columns];
    const draggedCol = newColumns[draggedIndex];
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, draggedCol);

    setColumns(newColumns);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Filter actions
  const toggleFilter = (colId) => {
    setActiveFilters(prev => ({
      ...prev,
      [colId]: !prev[colId]
    }));
  };

  const handleFilterChange = (colId, value) => {
    setColFilters(prev => ({
      ...prev,
      [colId]: value
    }));
    // Bidirectional sync: table column filter → pie chart filter
    if (colId === 'category') {
      setCategoryFilter(value);
      setTempCategoryFilter(value);
    }
    if (colId === 'status') {
      setStatusFilter(value);
      setTempStatusFilter(value);
    }
  };

  const clearAllColFilters = () => {
    setColFilters(defaultColFilters());
    setColSearchTexts(Object.fromEntries(COL_FILTER_IDS.map(k => [k, ''])));
    setActiveFilters({});
    // Also clear pie chart filters for full sync
    setCategoryFilter([]);
    setStatusFilter([]);
    setTempCategoryFilter([]);
    setTempStatusFilter([]);
  };

  const formatFilterDisplayValue = (val) => {
    if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
      try { return new Date(val).toLocaleDateString(); } catch { return val; }
    }
    return val;
  };

  const removeColFilterValue = (colId, val, e) => {
    e?.stopPropagation();
    const newValues = colFilters[colId].filter(x => x !== val);
    handleFilterChange(colId, newValues);
  };

  const hasActiveColFilters = COL_FILTER_IDS.some(id => colFilters[id]?.length > 0);

  const matchesColFilter = (filters, value, colId) => {
    if (!filters?.length) return true;
    const normalize = (v) => String(v ?? '').toLowerCase().trim().replace(/,/g, '');
    const haystack = normalize(value);
    return filters.some(f => {
      const needle = normalize(f);
      if (!needle) return true;
      // For experience: require whole-word / exact boundary match to prevent
      // "8 years" from matching "18 years" or "28 years"
      if (colId === 'experience') {
        // Try numeric prefix match: extract leading number from filter and value
        const filterNum = needle.match(/^(\d+(\.\d+)?)/);
        const valueNum = haystack.match(/^(\d+(\.\d+)?)/);
        if (filterNum && valueNum) {
          return filterNum[1] === valueNum[1];
        }
        // Fallback: whole-word boundary match
        const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(^|\\s)${escaped}(\\s|$)`).test(haystack);
      }
      return haystack.includes(needle);
    });
  };

  const COL_FILTER_KEYS = ['source','name','category','phone','qualification','experience','salary','preferred_location','secondary_location','preferred_block','client','status','worked_before','comment','last_update'];

  const applyBaseFilters = (list) => list.filter(emp => {
    const catMatch = categoryFilter.length === 0 || categoryFilter.includes(emp.category);
    const statMatch = statusFilter.length === 0 || statusFilter.includes(emp.status);
    const colMatch = COL_FILTER_KEYS.every(key => {
      const val = key === 'last_update' ? emp.updated_at : emp[key];
      return matchesColFilter(colFilters[key], val, key);
    });
    return catMatch && statMatch && colMatch;
  });

  const baseFilteredEmployees = applyBaseFilters(employees);
  const filteredEmployees = !colFilters.sno?.length
    ? baseFilteredEmployees
    : baseFilteredEmployees.filter((emp, idx) => matchesColFilter(colFilters.sno, String(idx + 1)));

  const buildExportParams = (includeVisibleCols = false) => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter.length) params.status = statusFilter;
    if (categoryFilter.length) params.category = categoryFilter;
    Object.entries(colFilters).forEach(([colId, val]) => {
      if (val?.length) params[`filter_${colId}`] = val;
    });
    // For CSV: only export visible columns (based on gear icon settings)
    if (includeVisibleCols) {
      const visibleIds = columns
        .map(c => c.id)
        .filter(id => !hiddenColumns.includes(id) && id !== 'actions' && id !== 'resume');
      params.visible_columns = visibleIds.join(',');
    }
    return params;
  };

  // Get visible column IDs for individual PDF downloads
  const getVisibleColIds = () => {
    return columns
      .map(c => c.id)
      .filter(id => !hiddenColumns.includes(id) && id !== 'actions' && id !== 'resume' && id !== 'sno');
  };

  const downloadResume = async (emp) => {
    try {
      const visibleCols = getVisibleColIds().join(',');
      // Always use /resume endpoint — serves uploaded file OR falls back to generated PDF
      // Pass visible_columns so hidden cols (e.g. salary) are excluded from the PDF
      const res = await axios.get(`/api/employees/${emp.id}/resume`, {
        params: { visible_columns: visibleCols },
        responseType: 'blob'
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+?)"/);
      const filename = match ? match[1] : `${emp.name.replace(/[^a-zA-Z0-9]/g, '_')}_Resume.pdf`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Resume download error:', err);
      toast.error('Failed to download resume');
    }
  };

  const renderCell = (colId, emp, index) => {
    switch (colId) {
      case 'sno': return <td key="sno" className="muted">#{index + 1}</td>;
      case 'source': return <td key="source">{emp.source || '—'}</td>;
      case 'name': return <td key="name" style={{fontWeight:600}}>{emp.name}</td>;
      case 'category': return <td key="category">{emp.category || '—'}</td>;
      case 'phone': return <td key="phone">{emp.phone || '—'}</td>;
      case 'qualification': return <td key="qualification">{emp.qualification || '—'}</td>;
      case 'worked_before': return <td key="worked_before" style={{ textAlign: 'center' }}>{emp.worked_before === 'Yes' ? <span style={{ color: 'var(--green)', fontWeight: 700, fontSize: '13px' }}>Alumni</span> : <span style={{ color: 'var(--text2)', fontSize: '13px' }}>No</span>}</td>;
      case 'experience': return <td key="experience">{emp.experience || '—'}</td>;
      case 'salary': return <td key="salary">{emp.salary ? emp.salary.replace(/^\$/, '') : '—'}</td>;
      case 'preferred_location': return <td key="preferred_location">{emp.preferred_location || '—'}</td>;
      case 'secondary_location': return <td key="secondary_location">{emp.secondary_location || '—'}</td>;
      case 'preferred_block': return <td key="preferred_block">{emp.preferred_block || '—'}</td>;
      case 'client': return <td key="client">{emp.client || '—'}</td>;
      case 'job_role': return <td key="job_role">{emp.job_role ? <span className="cat-badge">{emp.job_role}</span> : '—'}</td>;
      case 'status': return (
        <td key="status">
          <span className={`status-badge ${emp.status === 'Available' ? 'avail' : 'occ'}`}>{emp.status}</span>
        </td>
      );
      case 'resume': return (
        <td key="resume" style={{textAlign:'center'}}>
          <button
            className="btn-resume-icon"
            style={emp.resume_path ? {} : { opacity: 0.6, fontStyle: 'italic' }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadResume(emp); }}
            title={emp.resume_path ? 'Download uploaded resume' : 'Download auto-generated profile PDF'}
          >
            Download
          </button>
        </td>
      );
      case 'comment': return (
        <td key="comment" style={{minWidth:'160px',maxWidth:'320px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          <div className="comment-cell-wrap">
            <span className="comment-cell-text">{emp.comment || emp.notes || '—'}</span>
            {(emp.comment || emp.notes) && (
              <div className="comment-tooltip">{emp.comment || emp.notes}</div>
            )}
          </div>
        </td>
      );
      case 'last_update': return <td key="last_update" className="muted" style={{fontSize:'12px'}}>{emp.updated_at ? new Date(emp.updated_at).toLocaleDateString() : '—'}</td>;
      case 'actions': return (
        <td key="actions" className="actions-cell">
          <button className="btn-edit" onClick={() => setEditWorker(emp)}>Edit</button>
          <SaveButton employee={emp} visibleColumns={getVisibleColIds()} />
          <button className="btn-danger-sm" onClick={() => deleteWorker(emp.id)}>Delete</button>
        </td>
      );
      default: return null;
    }
  };

  const deleteWorker = async id => {
    if (!await showConfirm(`Delete "${id}" permanently? This cannot be undone.`, true)) return;
    await axios.delete(`/api/employees/${id}`);
    refresh();
  };

  const exportAll = async (type) => {
    setExportLoading(type);
    try {
      const url = `/api/employees/download/all-${type}`;
      // Pass visible_columns for both CSV and PDF exports (gear icon settings)
      const params = buildExportParams(type === 'csv' || type === 'pdf' || type === 'excel');
      const res = await axios.get(url, { params, responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+?)"/);
      const filename = match ? match[1] : `all-workers.${type === 'pdf' ? 'pdf' : 'csv'}`;
      const blob = new Blob([res.data]);
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = objUrl; a.download = filename; a.click();
      URL.revokeObjectURL(objUrl);
    } catch { toast.error('Export failed. Please try again.'); }
    finally { setExportLoading(''); }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Please select a valid Excel (.xlsx, .xls) or CSV file');
      e.target.value = '';
      return;
    }

    const toastId = toast.loading(`⏳ Uploading ${file.name}…`, { autoClose: false });
    const formData = new FormData();
    formData.append('file', file);
    try {
      const url = ext === 'csv' ? '/api/employees/import-csv' : '/api/employees/import-xlsx';
      const { data } = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (prog) => {
          const pct = Math.round((prog.loaded * 100) / (prog.total || prog.loaded));
          toast.update(toastId, { render: `⏳ Uploading… ${pct}%` });
        }
      });
      toast.update(toastId, {
        render: `✅ Imported ${data.imported} worker${data.imported !== 1 ? 's' : ''} successfully!`,
        type: 'success',
        isLoading: false,
        autoClose: 4000
      });
      refresh(); loadCategories();
    } catch (err) {
      toast.update(toastId, {
        render: `❌ ${err.response?.data?.error || 'Import failed'}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    }
    e.target.value = '';
  };

  // DB Snapshot download
  const handleSnapshot = async () => {
    setDbLoading('snapshot');
    try {
      const res = await axios.get('/api/db/snapshot', { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+?)"/);
      const filename = match ? match[1] : 'db-snapshot.xlsx';
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Snapshot download failed'); }
    finally { setDbLoading(''); }
  };

  // DB Restore from snapshot
  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!await showConfirm('This will import data from the snapshot. Existing data will NOT be deleted. Continue?')) {
      e.target.value = ''; return;
    }
    setDbLoading('restore');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post('/api/db/restore', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Database restored from snapshot!');
      refresh(); loadCategories();
      setShowManageDb(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Restore failed');
    }
    setDbLoading('');
    e.target.value = '';
  };



  const exportResumes = async () => {
    setExportLoading('resumes');
    try {
      const res = await axios.get('/api/employees/download/resumes', { params: buildExportParams(), responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+?)"/);
      const filename = match ? match[1] : 'resumes.zip';
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.error || 'No resumes available for download'); }
    finally { setExportLoading(''); }
  };

  const toggleColVisibility = (colId) => {
    setHiddenColumns(prev => {
      const next = prev.includes(colId) ? prev.filter(c => c !== colId) : [...prev, colId];
      localStorage.setItem('wf_hidden_cols', JSON.stringify(next));
      return next;
    });
  };

  const activeColumns = user?.role === 'read_only'
    ? columns.filter(col => col.id !== 'actions' && !hiddenColumns.includes(col.id))
    : columns.filter(col => !hiddenColumns.includes(col.id));

  const filteredAvail = filteredEmployees.filter(e => e.status === 'Available').length;
  const filteredOcc = filteredEmployees.filter(e => e.status === 'Occupied').length;
  const maxBar = Math.max(filteredAvail, filteredOcc, 1);
  const hasOpenColFilter = Object.values(activeFilters).some(Boolean);
  const needsTableScrollSpace = filteredEmployees.length === 0 || hasOpenColFilter;

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  // Refs for phantom horizontal scrollbar sync
  const tableWrapRef = useRef(null);
  const phantomRef = useRef(null);
  const phantomInnerRef = useRef(null);
  const phantomSyncRef = useRef(false);

  useEffect(() => {
    const wrap = tableWrapRef.current;
    const phantom = phantomRef.current;
    const inner = phantomInnerRef.current;
    if (!wrap || !phantom || !inner) return;
    const updateWidth = () => { inner.style.width = wrap.scrollWidth + 'px'; };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(wrap);
    const onWrapScroll = () => {
      if (phantomSyncRef.current) return;
      phantomSyncRef.current = true;
      phantom.scrollLeft = wrap.scrollLeft;
      phantomSyncRef.current = false;
    };
    const onPhantomScroll = () => {
      if (phantomSyncRef.current) return;
      phantomSyncRef.current = true;
      wrap.scrollLeft = phantom.scrollLeft;
      phantomSyncRef.current = false;
    };
    wrap.addEventListener('scroll', onWrapScroll);
    phantom.addEventListener('scroll', onPhantomScroll);
    return () => { ro.disconnect(); wrap.removeEventListener('scroll', onWrapScroll); phantom.removeEventListener('scroll', onPhantomScroll); };
  }, [view]);

  // Keyboard shortcut: / to focus search
  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !e.target.matches('input,textarea,select')) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowAdd(false);
        setEditWorker(null);
        setShowCatMgr(false);
        setConfirmModal(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="app" onClick={() => setShowColToggle(false)}>
      {/* ── Navbar ── */}
      <nav className="glass-navbar">
        <span className="nav-brand" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Visioncare" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
          Visioncare Man Power Management Database
        </span>
        <div className="nav-actions">
          <button className="glass-button" onClick={cycleColorTheme} title="Toggle Theme">
            {colorTheme === 'light' ? '🌙' : '☀️'}
          </button>
          <span className="nav-user" style={{ marginRight: '16px' }}>Hi, {user?.username}</span>
          <button className="glass-button secondary" onClick={handleLogout}>Sign out</button>
          {user?.role === 'admin' && (
            <button
              className={`nav-settings-btn ${view === 'iam' ? 'active' : ''}`}
              onClick={() => setView(v => v === 'iam' ? 'dashboard' : 'iam')}
              title={view === 'iam' ? "Back to Dashboard" : "IAM Controls"}
            >
              ⚙
            </button>
          )}
        </div>
      </nav>

      {view === 'iam' ? (
        <div className="iam-page-container">
          <div className="iam-page-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button className="btn-outline-sm" onClick={() => setView('dashboard')}>
                ← Back to Dashboard
              </button>
              <h2 className="iam-title" style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>IAM & Access Control</h2>
            </div>
            <div className="iam-page-tabs">
              <button className={`iam-page-tab-btn ${iamTab === 'users' ? 'active' : ''}`} onClick={() => setIamTab('users')}>
                User Directory
              </button>
              <button className={`iam-page-tab-btn ${iamTab === 'database' ? 'active' : ''}`} onClick={() => setIamTab('database')}>
                Database Utilities
              </button>
            </div>
          </div>

          <div className="iam-page-body">
            {iamTab === 'users' ? (
              <div className="iam-grid" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '16px' }}>
                {/* Left column: Add user and Security settings */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: '1', minWidth: '300px' }}>
                  <div className="iam-card" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px' }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '700', color: 'var(--text)' }}>Create User Access</h3>
                    <p className="card-desc" style={{ fontSize: '12px', color: 'var(--text2)', margin: '0 0 16px 0' }}>Grant login access to the administration dashboard by creating a user credentials set.</p>
                    {adminPanelError && <div className="error-msg" style={{ marginBottom: '14px', padding: '8px' }}>{adminPanelError}</div>}
                    <form onSubmit={handleAddUser}>
                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px', fontWeight: '600' }}>Username</label>
                        <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="e.g. jsmith" required />
                      </div>
                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px', fontWeight: '600' }}>Password</label>
                        <input type="password" value={newAdminPassword} onChange={e => setNewAdminPassword(e.target.value)} placeholder="Password" required />
                      </div>
                      <div className="form-group" style={{ marginBottom: '16px' }}>
                        <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px', fontWeight: '600' }}>Access Level</label>
                        <select value={newRole} onChange={e => setNewRole(e.target.value)} style={{ width: '100%', background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px' }}>
                          <option value="read_only">Read-only (Viewer)</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <button type="submit" className="btn-primary" style={{ width: '100%' }}>Create User</button>
                    </form>
                  </div>

                  <div className="iam-card" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px' }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '700', color: 'var(--text)' }}>Security Settings</h3>
                    <p className="card-desc" style={{ fontSize: '12px', color: 'var(--text2)', margin: '0 0 16px 0' }}>Configure security configurations and update account passwords.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <button className="btn-ghost" style={{ justifyContent: 'center' }} onClick={async () => {
                        setMfaError(''); setMfaCode('');
                        if (mfaEnabled) {
                          if (await showConfirm('Disable MFA for your account?', true)) {
                            await axios.post('/api/mfa/disable');
                            setMfaEnabled(false);
                            toast.success('MFA disabled.');
                          }
                        } else {
                          setMfaLoading(true);
                          try {
                            const { data } = await axios.post('/api/mfa/setup');
                            setMfaQr(data.qrDataUrl); setMfaSecret(data.secret);
                            setShowMfaSetup(true);
                          } catch { toast.error('MFA setup failed'); }
                          finally { setMfaLoading(false); }
                        }
                      }} disabled={mfaLoading}>
                        {mfaEnabled ? 'Disable MFA' : 'Enable MFA (Google Auth)'}
                      </button>
                      <button className="btn-ghost" style={{ justifyContent: 'center' }} onClick={() => { setShowChangePassword(true); }}>Change Password</button>
                    </div>
                  </div>
                </div>

                {/* Right column: Existing users directory list */}
                <div className="iam-card" style={{ flex: '1.5', minWidth: '340px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px' }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '700', color: 'var(--text)' }}>Existing Access & Users</h3>
                  <p className="card-desc" style={{ fontSize: '12px', color: 'var(--text2)', margin: '0 0 4px 0' }}>Active users authorized to access the system. Each user is provisioned with MFA automatically on creation.</p>
                  {mfaProvisionLoading && <p style={{ fontSize: '12px', color: 'var(--primary)', margin: '4px 0 8px 0' }}>Generating MFA QR code...</p>}
                  <div className="table-wrap" style={{ marginTop: '16px', border: '1px solid var(--border)' }}>
                    <table className="worker-table">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Role</th>
                          <th>MFA</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.id}>
                            <td style={{ fontWeight: 600 }}>{u.username}</td>
                            <td>
                              <span className={`role-badge ${u.role === 'admin' ? 'admin' : 'viewer'}`}>
                                {u.role === 'admin' ? 'Admin' : 'Read-only'}
                              </span>
                            </td>
                            <td>
                              <span style={{
                                fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px',
                                background: u.mfa_enabled ? 'rgba(34,197,94,0.15)' : 'rgba(156,163,175,0.15)',
                                color: u.mfa_enabled ? 'var(--green)' : 'var(--text2)'
                              }}>
                                {u.mfa_enabled ? 'Active' : 'Off'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {u.username === user?.username ? (
                                <span className="muted" style={{ fontSize: '11px', fontWeight: 600 }}>Current</span>
                              ) : (
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                  <button
                                    className="btn-outline-sm"
                                    onClick={() => handleMfaReenroll(u.id)}
                                    title={u.mfa_enabled ? 'Re-enroll MFA (new QR)' : 'Provision MFA'}
                                    disabled={mfaProvisionLoading}
                                    style={{ fontSize: '10px', padding: '3px 7px' }}
                                  >
                                    {u.mfa_enabled ? 'Re-enroll MFA' : 'Enroll MFA'}
                                  </button>
                                  {u.mfa_enabled && (
                                    <button
                                      className="btn-outline-sm"
                                      onClick={() => handleMfaRevoke(u.id)}
                                      title="Disable MFA for this user"
                                      style={{ fontSize: '10px', padding: '3px 7px', borderColor: 'var(--danger)', color: 'var(--danger)' }}
                                    >
                                      Revoke MFA
                                    </button>
                                  )}
                                  <button
                                    className="btn-danger-sm"
                                    onClick={() => handleDeleteUser(u.id)}
                                    title="Delete user"
                                    style={{ fontSize: '10px', padding: '3px 7px' }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="iam-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '16px' }}>
                <div className="iam-card" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px' }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '700', color: 'var(--text)' }}>Database Snapshot</h3>
                  <p className="card-desc" style={{ fontSize: '12px', color: 'var(--text2)', margin: '0 0 16px 0' }}>Download a full backup spreadsheet containing all employees, categories, work history, and access users.</p>
                  <button className="btn-primary" onClick={handleSnapshot} disabled={!!dbLoading} style={{ marginTop: '14px', width: '100%' }}>
                    {dbLoading === 'snapshot' ? 'Exporting snapshot...' : 'Download Backup (.xlsx)'}
                  </button>
                </div>

                <div className="iam-card" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px' }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '700', color: 'var(--text)' }}>Restore from snapshot</h3>
                  <p className="card-desc" style={{ fontSize: '12px', color: 'var(--text2)', margin: '0 0 16px 0' }}>Upload a previously generated backup snapshot spreadsheet (.xlsx) to restore all system and user states.</p>
                  <label className="btn-primary" style={{ display: 'block', textAlign: 'center', cursor: dbLoading ? 'not-allowed' : 'pointer', opacity: dbLoading ? 0.6 : 1, width: '100%', marginTop: '14px' }}>
                    {dbLoading === 'restore' ? 'Restoring states...' : 'Choose Backup File'}
                    <input type="file" accept=".xlsx" onChange={handleRestore} disabled={!!dbLoading} style={{ display: 'none' }} />
                  </label>
                </div>

              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* ── Stats Row: Filter Panel + Pie Chart ── */}
          <div style={{ display: 'flex', gap: '12px', margin: '0 0 0 0', padding: '10px 16px 0', alignItems: 'stretch', flexWrap: 'wrap' }}>

            {/* LEFT: Filter Workers card */}
            <div className="glass-card" style={{ flex: '0 0 280px', minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '0', padding: 0, overflow: 'visible', zIndex: 300 }}>
              {/* Header section with gradient */}
              <div style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0.03) 100%)', padding: '12px 16px 10px', borderBottom: '1px solid var(--glass-border)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                  <span style={{ fontSize: '36px', fontWeight: 800, color: 'var(--glass-text)', lineHeight: 1 }}>{stats.total || 0}</span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: 'var(--green)', fontWeight: 700, background: 'rgba(115,191,105,0.12)', padding: '2px 8px', borderRadius: '10px' }}>● {stats.available || 0} avail</span>
                    <span style={{ fontSize: '11px', color: 'var(--orange)', fontWeight: 700, background: 'rgba(251,191,36,0.12)', padding: '2px 8px', borderRadius: '10px' }}>● {stats.occupied || 0} occ</span>
                  </div>
                </div>
              </div>

              {/* Filter section */}
              <div style={{ padding: '12px 16px', overflow: 'visible' }}>

                {/* Job Role dropdown */}
                <div className="cat-dropdown-container" style={{ position: 'relative', marginBottom: '10px', zIndex: showCatDrop ? 400 : 300 }}>
                  <label style={{ marginBottom: '4px', display: 'block', fontSize: '11px', color: 'var(--glass-text-secondary)', fontWeight: 'bold' }}>JOB ROLE</label>
                  <div onClick={() => setShowCatDrop(!showCatDrop)} className="glass-input" style={{ width: '100%', cursor: 'pointer', minHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', fontSize: '13px' }}>
                    <span>{tempCategoryFilter.length === 0 ? 'All' : `${tempCategoryFilter.length} selected`}</span>
                    <span style={{ fontSize: '10px', color: 'var(--glass-text-secondary)' }}>{showCatDrop ? '▲' : '▼'}</span>
                  </div>
                  {showCatDrop && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, minWidth: '100%', background: 'var(--bg2, #1e293b)', border: '1px solid var(--border)', zIndex: 9999, maxHeight: '220px', overflowY: 'auto', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                      {categories.map(c => {
                        const isSelected = tempCategoryFilter.includes(c);
                        return (
                          <div key={c} className="col-toggle-item" style={{ padding: '8px 10px' }} onClick={(e) => {
                            e.stopPropagation();
                            if (!isSelected) setTempCategoryFilter(prev => [...prev, c]);
                            else setTempCategoryFilter(prev => prev.filter(x => x !== c));
                          }}>
                            <span className="col-toggle-label" style={{ fontSize: '14px', textTransform: 'none', fontWeight: 'normal' }}>{c}</span>
                            <div className={`col-toggle-switch ${isSelected ? 'on' : 'off'}`}>
                              <div className="col-toggle-thumb" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {categoryFilter.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                      {categoryFilter.map(c => (
                        <span key={c} className="glass-status-badge" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', padding: '2px 6px' }}>
                          {c}
                          <span style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 'bold' }} onClick={(e) => { e.stopPropagation(); setCategoryFilter(p => p.filter(x => x !== c)); setTempCategoryFilter(p => p.filter(x => x !== c)); }}>×</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Status dropdown */}
                <div className="status-dropdown-container" style={{ position: 'relative', marginBottom: '10px', zIndex: showStatusDrop ? 400 : 200 }}>
                  <label style={{ marginBottom: '4px', display: 'block', fontSize: '11px', color: 'var(--glass-text-secondary)', fontWeight: 'bold' }}>STATUS</label>
                  <div onClick={() => setShowStatusDrop(!showStatusDrop)} className="glass-input" style={{ width: '100%', cursor: 'pointer', minHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', fontSize: '13px' }}>
                    <span>{tempStatusFilter.length === 0 ? 'All' : `${tempStatusFilter.length} selected`}</span>
                    <span style={{ fontSize: '10px', color: 'var(--glass-text-secondary)' }}>{showStatusDrop ? '▲' : '▼'}</span>
                  </div>
                  {showStatusDrop && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, minWidth: '100%', background: 'var(--bg2, #1e293b)', border: '1px solid var(--border)', zIndex: 9999, padding: '10px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                      {['Available', 'Occupied'].map(s => {
                        const isSelected = tempStatusFilter.includes(s);
                        return (
                          <div key={s} className="col-toggle-item" style={{ padding: '8px 10px' }} onClick={(e) => {
                            e.stopPropagation();
                            if (!isSelected) setTempStatusFilter(prev => [...prev, s]);
                            else setTempStatusFilter(prev => prev.filter(x => x !== s));
                          }}>
                            <span className="col-toggle-label" style={{ fontSize: '14px', textTransform: 'none', fontWeight: 'normal' }}>{s}</span>
                            <div className={`col-toggle-switch ${isSelected ? 'on' : 'off'}`}>
                              <div className="col-toggle-thumb" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {statusFilter.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                      {statusFilter.map(s => (
                        <span key={s} className="glass-status-badge" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', padding: '2px 6px' }}>
                          {s}
                          <span style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 'bold' }} onClick={(e) => { e.stopPropagation(); setStatusFilter(p => p.filter(x => x !== s)); setTempStatusFilter(p => p.filter(x => x !== s)); }}>×</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                {filterError && (
                  <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: 600, marginTop: '12px', padding: '6px 10px', background: 'rgba(239,68,68,0.08)', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.2)' }}>
                    ⚠ Please select at least one Job Role before applying filters.
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button className="glass-button secondary" style={{ flex: 1, fontSize: '12px', padding: '6px 10px' }} onClick={() => { setCategoryFilter([]); setStatusFilter([]); setTempCategoryFilter([]); setTempStatusFilter([]); setFilterError(false); setColFilters(prev => ({ ...prev, category: [], status: [] })); }}>Clear</button>
                  <button className="glass-button primary" style={{ flex: 2, fontSize: '12px', padding: '6px 10px' }} onClick={() => {
                    setFilterError(false);
                    setCategoryFilter(tempCategoryFilter);
                    setStatusFilter(tempStatusFilter);
                    // Bidirectional sync: pie chart filter → table column filter
                    setColFilters(prev => ({ ...prev, category: tempCategoryFilter, status: tempStatusFilter }));
                    setShowCatDrop(false);
                    setShowStatusDrop(false);
                  }}>Apply Filters</button>
                </div>
              </div>
            </div>

            {/* RIGHT: Charts (Global Availability + Filtered Categories) */}
            <div className="glass-card blur-ok" style={{ flex: '1 1 0', minWidth: '420px', display: 'flex', gap: '0', padding: 0, overflow: 'hidden' }}>
              
              {/* CHART 1: ALL WORKERS (Availability, Unfiltered) */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '16px 20px' }}>
                <div className="stat-label" style={{ fontSize: '10px', letterSpacing: '1.2px', marginBottom: '12px', opacity: 0.7 }}>TOTAL WORKFORCE AVAILABILITY</div>
                {(() => {
                  const avail = stats.available || 0;
                  const occ = stats.occupied || 0;
                  const total = stats.total || 0;
                  const r = 48;
                  const cx = 58;
                  const cy = 58;
                  const strokeWidth = 16;
                  const circumference = 2 * Math.PI * r;
                  const availFrac = total > 0 ? avail / total : 0.5;
                  const occFrac = total > 0 ? occ / total : 0.5;
                  const availDash = availFrac * circumference;
                  const occDash = occFrac * circumference;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%', justifyContent: 'center' }}>
                      <svg width="116" height="116" viewBox="0 0 116 116" style={{ flexShrink: 0 }}>
                        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={strokeWidth} />
                        {total > 0 && (
                          <>
                            <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--green)" strokeWidth={strokeWidth} strokeDasharray={`${availDash} ${circumference - availDash}`} strokeDashoffset={circumference / 4} strokeLinecap="butt" style={{ transition: 'stroke-dasharray 0.5s ease' }}>
                              <title>Available: {avail}</title>
                            </circle>
                            <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--orange)" strokeWidth={strokeWidth} strokeDasharray={`${occDash} ${circumference - occDash}`} strokeDashoffset={circumference / 4 - availDash} strokeLinecap="butt" style={{ transition: 'stroke-dasharray 0.5s ease' }}>
                              <title>Occupied: {occ}</title>
                            </circle>
                          </>
                        )}
                        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="var(--text)" fontSize="26" fontWeight="800">{total}</text>
                      </svg>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--green)' }} />
                          <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>{avail}</span>
                          <span style={{ fontSize: '10px', color: 'var(--text2)', fontWeight: 700 }}>AVAILABLE</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--orange)' }} />
                          <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>{occ}</span>
                          <span style={{ fontSize: '10px', color: 'var(--text2)', fontWeight: 700 }}>OCCUPIED</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* DIVIDER between charts */}
              <div style={{ width: '1px', background: 'var(--border)', alignSelf: 'stretch', margin: '14px 0' }} />

              {/* CHART 2: FILTERED BY CATEGORY */}
              <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '16px 20px' }}>
                <div className="stat-label" style={{ fontSize: '10px', letterSpacing: '1.2px', marginBottom: '12px', opacity: 0.7 }}>BY CATEGORY</div>
                {(() => {
                  const totalFiltered = filteredEmployees.length;
                  const catCounts = {};
                  filteredEmployees.forEach(emp => {
                    const cat = emp.category || 'Uncategorized';
                    catCounts[cat] = (catCounts[cat] || 0) + 1;
                  });
                  const catData = Object.keys(catCounts).map(cat => ({ name: cat, count: catCounts[cat] })).sort((a,b) => b.count - a.count);
                  const COLORS = ['var(--primary)', 'var(--green)', 'var(--orange)', '#8b5cf6', '#ec4899', '#06b6d4', '#f43f5e', '#14b8a6', '#eab308'];
                  
                  const r = 48;
                  const cx = 58;
                  const cy = 58;
                  const strokeWidth = 16;
                  const circumference = 2 * Math.PI * r;
                  
                  let currentOffset = circumference / 4;

                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', width: '100%', justifyContent: 'center' }}>
                      <svg width="116" height="116" viewBox="0 0 116 116" style={{ flexShrink: 0 }}>
                        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={strokeWidth} />
                        {totalFiltered > 0 && catData.map((item, i) => {
                          const frac = item.count / totalFiltered;
                          const dash = frac * circumference;
                          const offset = currentOffset;
                          currentOffset -= dash;
                          return (
                            <circle
                              key={item.name}
                              cx={cx} cy={cy} r={r}
                              fill="none"
                              stroke={COLORS[i % COLORS.length]}
                              strokeWidth={strokeWidth}
                              strokeDasharray={`${dash} ${circumference - dash}`}
                              strokeDashoffset={offset}
                              strokeLinecap="butt"
                              style={{ transition: 'stroke-dasharray 0.5s ease' }}
                            >
                              <title>{item.name}: {item.count}</title>
                            </circle>
                          );
                        })}
                        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="var(--text)" fontSize="26" fontWeight="800">{totalFiltered}</text>
                      </svg>
                      
                      {/* Legend fills remaining space */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 16px', flex: 1 }}>
                        {catData.length === 0 && <div style={{ fontSize: '11px', color: 'var(--text2)' }}>No data</div>}
                        {catData.map((item, i) => (
                          <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                            <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text)' }}>{item.count}</span>
                            <span style={{ fontSize: '10px', color: 'var(--text2)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }} title={item.name}>{item.name.toUpperCase()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

          </div>

      {/* ── Table ── */}
      <div className="table-card">
        <div className="table-toolbar">
          <div className="toolbar-left" style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <h2>All Workers</h2>
              <div style={{fontSize:'12px',color:'var(--text2)',marginTop:'3px',fontWeight:500}}>
                Showing <strong style={{color:'var(--primary)'}}>{filteredEmployees.length}</strong> of <strong>{employees.length}</strong> workers
                {categoryFilter.length > 0 && <span> in <em>{categoryFilter.join(', ')}</em></span>}
                {statusFilter.length > 0 && <span> • {statusFilter.join(', ')}</span>}
                {COL_FILTER_IDS.some(id => colFilters[id]?.length > 0) && (
                  <span> • column filters active</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input
                ref={searchRef}
                className="search-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name… (press /)"
              />
              {user?.role === 'admin' && (
                <button className="btn-ghost" onClick={() => setShowCatMgr(true)}>+ Job Role</button>
              )}
              {user?.role === 'admin' && (
                <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add Worker</button>
              )}
              {hasActiveColFilters && (
                <button
                  className="btn-ghost toolbar-clear-col-filters"
                  onClick={clearAllColFilters}
                  title="Clear all column filters"
                >
                  Clear column filters
                </button>
              )}
              {/* ── Column Visibility Gear ── */}
              <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                <button
                  className={`nav-settings-btn${showColToggle ? ' active' : ''}`}
                  onClick={() => setShowColToggle(v => !v)}
                  title="Show/hide columns"
                >
                  ⚙
                </button>
                {showColToggle && (
                  <div className="col-toggle-panel" onClick={e => e.stopPropagation()}>
                    <div className="col-toggle-header">
                      <span>Columns</span>
                      <button
                        className="col-toggle-reset"
                        onClick={() => { setHiddenColumns([]); localStorage.removeItem('wf_hidden_cols'); }}
                      >Reset</button>
                    </div>
                    <div className="col-toggle-list">
                      {columns
                        .filter(col => col.id !== 'sno' && col.id !== 'actions')
                        .map(col => {
                          const isVisible = !hiddenColumns.includes(col.id);
                          return (
                            <div
                              key={col.id}
                              className="col-toggle-item"
                              onClick={() => toggleColVisibility(col.id)}
                            >
                              <span className="col-toggle-label">{col.label}</span>
                              <div className={`col-toggle-switch ${isVisible ? 'on' : 'off'}`}>
                                <div className="col-toggle-thumb" />
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="toolbar-right">
            {/* Single "Export / Import" dropdown — collapses 4 buttons into 1 */}
            <ExportMenu
              user={user}
              exportLoading={exportLoading}
              exportResumes={exportResumes}
              exportAll={exportAll}
              handleImportExcel={handleImportExcel}
            />
          </div>
        </div>



        {/* Phantom fixed scrollbar — always at bottom of viewport, synced to table */}
        <div className="phantom-hscroll" ref={phantomRef}>
          <div className="phantom-hscroll-inner" ref={phantomInnerRef} />
        </div>

        <div className="table-scroll-outer">
        <div ref={tableWrapRef} className={`table-wrap${needsTableScrollSpace ? ' table-wrap-scroll-space' : ''}`} style={{ overflowX: 'scroll' }}>
          <table className="worker-table" style={{ tableLayout: 'auto', width: '100%' }}>
            <thead>
              <tr>
                {activeColumns.map((col, index) => (
                  <th
                    key={col.id}
                    draggable={!resizingCol}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`draggable-header col-dropdown-container ${activeFilters[col.id] ? 'filter-open' : ''} ${colFilters[col.id]?.length ? 'has-col-filter' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
                    style={{ 
                      cursor: resizingCol ? 'col-resize' : 'grab',
                      whiteSpace: col.id === 'comment' ? 'normal' : 'nowrap',
                      ...(colWidths[col.id] ? { width: colWidths[col.id], minWidth: colWidths[col.id] } : { minWidth: DEFAULT_COL_WIDTHS[col.id] || 60 })
                    }}
                  >
                    <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, col.id)} />
                    <div
                      className={`th-content${colFilters[col.id]?.length ? ' has-filters' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); if (col.id !== 'actions' && col.id !== 'resume') toggleFilter(col.id); }}
                    >
                      <div className="th-label-row">
                        <span>{col.label}</span>
                        {col.id !== 'actions' && col.id !== 'resume' && (
                          <button
                            className={`filter-btn ${colFilters[col.id]?.length ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFilter(col.id);
                            }}
                            title={`Filter by ${col.label}`}
                          >
                            ▼
                          </button>
                        )}
                      </div>
                      {col.id !== 'actions' && col.id !== 'resume' && colFilters[col.id]?.length > 0 && (
                        <div className="th-filter-chips" onClick={(e) => e.stopPropagation()}>
                          {colFilters[col.id].map(val => (
                            <span key={val} className="th-filter-chip" title={val}>
                              <span className="th-filter-chip-text">{formatFilterDisplayValue(val)}</span>
                              <button
                                type="button"
                                className="th-filter-chip-remove"
                                onClick={(e) => removeColFilterValue(col.id, val, e)}
                                title={`Remove ${val}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {activeFilters[col.id] && col.id !== 'actions' && col.id !== 'resume' && (
                      <div className="filter-popover" onClick={(e) => e.stopPropagation()} style={{padding: '12px', width: '220px', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        <input
                          className="col-filter-input"
                          value={colSearchTexts[col.id]}
                          onChange={(e) => setColSearchTexts(prev => ({...prev, [col.id]: e.target.value}))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && colSearchTexts[col.id]) {
                              handleFilterChange(col.id, [...(colFilters[col.id] || []), colSearchTexts[col.id]]);
                              setColSearchTexts(prev => ({...prev, [col.id]: ''}));
                            }
                          }}
                          placeholder={`Search ${col.label}...`}
                          autoFocus
                        />
                        <div className="filter-suggestions">
                          {(col.id === 'sno'
                            ? baseFilteredEmployees.map((_, i) => String(i + 1))
                            : Array.from(new Set(employees.map(e => {
                                const val = col.id === 'last_update' ? e.updated_at : e[col.id];
                                return String(val || '');
                              }).filter(Boolean)))
                          )
                            .filter(v => v.toLowerCase().includes((colSearchTexts[col.id] || '').toLowerCase()))
                            .map(val => (
                              <div
                                key={val}
                                className={`filter-suggestion-item${colFilters[col.id]?.includes(val) ? ' selected' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const isSelected = colFilters[col.id]?.includes(val);
                                  if (!isSelected) handleFilterChange(col.id, [...(colFilters[col.id] || []), val]);
                                  else handleFilterChange(col.id, colFilters[col.id].filter(x => x !== val));
                                }}
                              >
                                {val}
                              </div>
                            ))}
                        </div>
                        <div className="filter-popover-spacer" aria-hidden="true" />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 && (
                <tr><td colSpan={activeColumns.length} className="empty-row">No workers found.</td></tr>
              )}
              {filteredEmployees.map((emp, index) => (
                <tr key={emp.id}>
                  {activeColumns.map(col => renderCell(col.id, emp, index))}
                </tr>
              ))}
              <tr className="table-row-spacer" aria-hidden="true">
                <td colSpan={activeColumns.length} />
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>
      </>
      )}

      {/* ── Modals ── */}
      {showAdd && (
        <WorkerModal
          categories={categories}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
      {editWorker && (
        <WorkerModal
          worker={editWorker}
          categories={categories}
          onClose={() => setEditWorker(null)}
          onSaved={() => { setEditWorker(null); refresh(); }}
        />
      )}
      {showCatMgr && (
        <CategoryManager
          onClose={() => setShowCatMgr(false)}
          onChanged={() => { loadCategories(); loadStats(); }}
        />
      )}

      {/* ── Change Password Modal ── */}
      {showChangePassword && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="modal-close" onClick={() => { setShowChangePassword(false); setChangePasswordError(''); }}>✕</button>
            </div>
            <form onSubmit={handleChangePasswordSubmit} className="modal-form" style={{ padding: '20px 24px' }}>
              {changePasswordError && <div className="error-msg" style={{ marginBottom: '14px' }}>{changePasswordError}</div>}
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Current Password</label>
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button type="button" className="btn-ghost" onClick={() => { setShowChangePassword(false); setChangePasswordError(''); }}>Cancel</button>
                <button type="submit" className="btn-primary">Update Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MFA Setup Modal ── */}
      {showMfaSetup && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Setup Google Authenticator</h2>
              <button className="modal-close" onClick={() => { setShowMfaSetup(false); setMfaQr(''); setMfaSecret(''); setMfaCode(''); setMfaError(''); }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text2)' }}>1. Open Google Authenticator on your phone.<br/>2. Tap <strong>+</strong> → <strong>Scan QR code</strong>.<br/>3. Enter the 6-digit code below to confirm.</p>
              {mfaQr && <img src={mfaQr} alt="QR Code" style={{ width: '200px', height: '200px', margin: '0 auto', display: 'block', borderRadius: '8px' }} />}
              <p style={{ fontSize: '11px', color: 'var(--text3)', textAlign: 'center', wordBreak: 'break-all' }}>Manual key: <code>{mfaSecret}</code></p>
              {mfaError && <div className="error-msg">{mfaError}</div>}
              <label>6-Digit Code
                <input
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="000000"
                  style={{ textAlign: 'center', fontSize: '22px', letterSpacing: '8px', fontFamily: 'monospace' }}
                  maxLength={6} autoFocus
                />
              </label>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button className="btn-ghost" onClick={() => { setShowMfaSetup(false); setMfaQr(''); setMfaSecret(''); setMfaCode(''); setMfaError(''); }}>Cancel</button>
                <button className="btn-primary" disabled={mfaCode.length < 6 || mfaLoading} onClick={async () => {
                  setMfaLoading(true); setMfaError('');
                  try {
                    await axios.post('/api/mfa/verify', { code: mfaCode });
                    setMfaEnabled(true); setShowMfaSetup(false);
                    setMfaQr(''); setMfaSecret(''); setMfaCode('');
                    toast.success("MFA enabled! You'll need your authenticator on next login.");
                  } catch (e) { setMfaError(e.response?.data?.error || 'Invalid code'); }
                  finally { setMfaLoading(false); }
                }}>{mfaLoading ? 'Verifying…' : 'Verify & Enable'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MFA Provision Modal (Admin View) ── */}
      {mfaProvisionModal && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>MFA Provisioned: {mfaProvisionModal.username}</h2>
              <button className="modal-close" onClick={() => setMfaProvisionModal(null)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text2)', margin: 0 }}>
                Please share this QR code or setup key with the user securely. They need to scan it with their Google Authenticator app.
              </p>
              {mfaProvisionModal.qrDataUrl && (
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', margin: '0 auto' }}>
                  <img src={mfaProvisionModal.qrDataUrl} alt="QR Code" style={{ width: '200px', height: '200px', display: 'block' }} />
                </div>
              )}
              <p style={{ fontSize: '12px', color: 'var(--text3)', textAlign: 'center', wordBreak: 'break-all', margin: 0 }}>
                Manual Setup Key: <br/>
                <code style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text)' }}>{mfaProvisionModal.secret}</code>
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button className="btn-primary" onClick={() => setMfaProvisionModal(null)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Inline Confirm Modal ── */}
      {confirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-box small" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>{confirmModal.danger ? '⚠ Confirm Action' : 'Confirm'}</h2>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '14px', color: 'var(--text)', margin: 0, lineHeight: 1.6 }}>{confirmModal.message}</p>
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={confirmModal.onCancel}>Cancel</button>
              <button
                className="btn-primary"
                style={confirmModal.danger ? { background: '#dc2626' } : {}}
                onClick={confirmModal.onConfirm}
                autoFocus
              >
                {confirmModal.danger ? 'Yes, proceed' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sign Out Confirmation Modal ── */}
      {showLogoutConfirm && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-box small">
            <div className="modal-header">
              <h2>Confirm Sign Out</h2>
              <button className="close-btn" onClick={() => setShowLogoutConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '14px', color: 'var(--text)', margin: 0 }}>Are you sure you want to sign out?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
              <button className="btn-primary" style={{ background: '#dc2626' }} onClick={() => { setShowLogoutConfirm(false); logout(); }}>Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
